import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getBlogPostBySlug, getBlogPosts } from "../utils/blogStorage";
import { useAuth } from "../contexts/AuthContext";
import { sanitizeBlogHtml } from "../utils/blogContent";
import {
  Calendar,
  Clock,
  User,
  Tag,
  Share2,
  Edit2,
  BookOpen,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadPost = async () => {
      const loadedPost = await getBlogPostBySlug(slug);
      setPost(loadedPost);
      
      if (loadedPost) {
        const allPosts = await getBlogPosts();
        const related = allPosts
          .filter((p) => p.id !== loadedPost.id && p.category === loadedPost.category)
          .slice(0, 3);
        setRelatedPosts(related);
      }
      
      setIsLoading(false);
    };
    loadPost();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Blog Post Not Found
          </h2>
          <Link to="/blog" className="text-purple-600 hover:text-purple-700">
            Return to Blog
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link Copied!",
        description: "Blog post link copied to clipboard.",
      });
    }
  };

  const handleEdit = () => {
    navigate("/admin/blog", { state: { editingPost: post } });
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{
            background:
              "linear-gradient(135deg, rgb(238, 61, 60), rgb(36, 36, 105))",
          }}>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-48 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl mb-6">
              <BookOpen className="w-10 h-10 text-white" />
            </div>
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-full">
                {post.category}
              </span>
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {post.title}
            </h1>

            <p className="text-xl md:text-2xl text-white text-opacity-90 max-w-3xl mx-auto">
              {post.excerpt}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-6 text-white/90 mt-6">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(post.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>{post.author}</span>
              </div>
            </div>

            {isAuthenticated && (
              <div className="mt-6">
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Edit Post</span>
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Blog Post Content */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
          </motion.div>

          {/* Author Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center gap-4 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl mb-12 border border-purple-100"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center text-white text-2xl font-bold">
              {post.author.charAt(0)}
            </div>
            <div className="flex-grow">
              <h3 className="text-lg font-bold text-gray-900">{post.author}</h3>
              <p className="text-gray-600">{post.role}</p>
            </div>
            <button
              onClick={handleShare}
              className="p-3 bg-white rounded-lg shadow-md hover:shadow-lg transition-all text-purple-600"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-none"
          >
            <div
              className="blog-rich-content text-gray-700"
              dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(post.content) }}
            />
          </motion.div>

          {/* Tags */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 pt-8 border-t border-gray-200"
          >
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-bold text-gray-900">Tags</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {post.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-purple-100 hover:text-purple-700 transition-colors cursor-pointer"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Share Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-12 p-6 bg-gray-50 rounded-xl"
          >
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Share this article
            </h3>
            <div className="flex gap-3">
              <button
                onClick={handleShare}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                Share Link
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Related Articles
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  <div className="p-6">
                    <span className="text-xs font-semibold text-purple-600 mb-2 block">
                      {relatedPost.category}
                    </span>
                    <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-purple-700 transition-colors">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
